import React, { useContext, useState, useEffect } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { Package, Calendar, CheckCircle, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import * as api from '../services/fbaInboundApi';

const Step2_CreateInboundPlan = () => {
  const {
    outboundShipment,
    outboundItems,
    createPlan,
    nextStep,
    planId,
    plan,
    loading,
    statusMessage,
    setPlanId,
    refreshPlan,
    markStepCompleted,
    resetWorkflow
  } = useContext(FBAInboundContext);

  // Mark step as completed when plan already exists
  useEffect(() => {
    if (planId && plan?.items?.length > 0) {
      markStepCompleted(2);
    }
  }, [planId, plan?.items?.length, markStepCompleted]);

  const [items, setItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Initialize items from outbound
  useEffect(() => {
    if (outboundItems && outboundItems.length > 0) {
      const formattedItems = outboundItems.map(item => ({
        msku: item.item?.merchant_sku || item.merchant_sku || '',
        asin: item.item?.asin || item.asin || '',
        title: item.item?.name || item.title || '',
        quantity: item.quantity || 1,
        expiration: '',
        label_owner: 'SELLER',
        prep_owner: 'SELLER',
        requiresExpiration: checkRequiresExpiration(item)
      }));
      setItems(formattedItems);
    }
  }, [outboundItems]);

  // Check if item requires expiration date (groceries, supplements, etc.)
  const checkRequiresExpiration = (item) => {
    const title = (item.item?.name || item.title || '').toLowerCase();
    const keywords = ['food', 'grocery', 'supplement', 'vitamin', 'snack', 'beverage', 'organic'];
    return keywords.some(kw => title.includes(kw));
  };

  const handleExpirationChange = (index, value) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, expiration: value } : item
    ));
  };

  const handleQuantityChange = (index, value) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, quantity: parseInt(value) || 1 } : item
    ));
  };

  const handleSubmit = async () => {
    // Validate required expirations
    const missingExpiration = items.some(item =>
      item.requiresExpiration && !item.expiration
    );

    if (missingExpiration) {
      alert('Please provide expiration dates for all items that require them.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get channel_id from outbound (default to 126 if not found)
      const channelId = outboundShipment?.channel_id || 126;

      await createPlan(channelId, items.map(item => ({
        msku: item.msku,
        quantity: item.quantity,
        expiration: item.expiration || null,
        label_owner: item.label_owner,
        prep_owner: item.prep_owner
      })));

      setSuccess(true);
      setTimeout(() => {
        nextStep();
      }, 1500);
    } catch (err) {
      console.error('Failed to create plan:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!planId) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this plan? This will clear all progress and you will need to start over.'
    );

    if (!confirmed) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      // Cancel plan returns an operation ID
      const result = await api.deletePlan(planId);
      const operationId = typeof result === 'string' ? result : result.data?.id || result.operation_id;

      if (operationId) {
        // Poll until operation completes
        let attempts = 0;
        const maxAttempts = 30;

        while (attempts < maxAttempts) {
          const opStatus = await api.checkOperationStatus(operationId);
          console.log('Cancel operation status:', opStatus);

          if (opStatus.data?.status !== 'IN_PROGRESS') {
            break;
          }

          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }
      }

      // Reset the entire workflow to Step 0
      resetWorkflow();
      setSuccess(false);
    } catch (err) {
      console.error('Failed to delete plan:', err);
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Show loading state while creating plan OR when showing success briefly
  // This must come BEFORE the planId check so we show progress during creation
  if (isSubmitting || success) {
    const allComplete = success || (!statusMessage);

    return (
      <div className="text-center py-16">
        {success ? (
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
        ) : (
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
        )}
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {success ? 'Plan Created Successfully!' : 'Creating Inbound Plan'}
        </h3>
        <p className="text-gray-600 mb-4">
          {success ? 'Packing options received. Proceeding to next step...' : (statusMessage || 'Initializing...')}
        </p>
        <div className="max-w-md mx-auto">
          <div className="bg-gray-100 rounded-lg p-4 text-left text-sm space-y-2">
            <div className={`flex items-center ${statusMessage?.includes('Creating') ? 'text-blue-600 font-medium' : (statusMessage || success) ? 'text-green-600' : 'text-gray-400'}`}>
              {statusMessage?.includes('Creating') ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (statusMessage || success) ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <div className="w-4 h-4 mr-2 rounded-full border-2 border-gray-300" />
              )}
              Creating plan with Amazon
            </div>
            <div className={`flex items-center ${statusMessage?.includes('Amazon') && !statusMessage?.includes('Creating') ? 'text-blue-600 font-medium' : (statusMessage?.includes('packing') || statusMessage?.includes('Generating') || success) ? 'text-green-600' : 'text-gray-400'}`}>
              {statusMessage?.includes('Amazon') && !statusMessage?.includes('Creating') ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (statusMessage?.includes('packing') || statusMessage?.includes('Generating') || success) ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <div className="w-4 h-4 mr-2 rounded-full border-2 border-gray-300" />
              )}
              Waiting for Amazon to process
            </div>
            <div className={`flex items-center ${statusMessage?.includes('Generating') ? 'text-blue-600 font-medium' : (statusMessage?.includes('Waiting for packing') || success) ? 'text-green-600' : 'text-gray-400'}`}>
              {statusMessage?.includes('Generating') ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (statusMessage?.includes('Waiting for packing') || success) ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <div className="w-4 h-4 mr-2 rounded-full border-2 border-gray-300" />
              )}
              Generating packing options
            </div>
            <div className={`flex items-center ${statusMessage?.includes('Waiting for packing') ? 'text-blue-600 font-medium' : success ? 'text-green-600' : 'text-gray-400'}`}>
              {statusMessage?.includes('Waiting for packing') ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : success ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <div className="w-4 h-4 mr-2 rounded-full border-2 border-gray-300" />
              )}
              Receiving packing options
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If plan already exists (and we're not in the loading/success state), show with delete option
  if (planId) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Inbound Plan Created
        </h3>
        <p className="text-gray-600">
          Plan ID: {planId}
        </p>

        {/* Delete Plan Option */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          {deleteError && (
            <p className="text-sm text-red-600 mb-3">{deleteError}</p>
          )}
          <button
            onClick={handleDeletePlan}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Plan & Start Over
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Use this if the plan is in a bad state or you need to start fresh.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Package className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Create Inbound Plan</h4>
            <p className="text-sm text-blue-700 mt-1">
              Review the items below and add expiration dates where required.
              Items marked with a calendar icon require expiration dates.
            </p>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Items ({items.length})</h3>
            <span className="text-sm text-gray-500">
              Total Quantity: {items.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {items.map((item, index) => (
            <div key={index} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                      {item.msku}
                    </span>
                    {item.requiresExpiration && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                        <Calendar className="w-3 h-3 mr-1" />
                        Requires Expiry
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 truncate">
                    {item.title}
                  </p>
                  {item.asin && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      ASIN: {item.asin}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-4 ml-4">
                  {/* Quantity */}
                  <div className="w-24">
                    <label className="block text-xs text-gray-500 mb-1">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-center"
                    />
                  </div>

                  {/* Expiration Date */}
                  <div className="w-40">
                    <label className="block text-xs text-gray-500 mb-1">
                      Expiration
                      {item.requiresExpiration && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      value={item.expiration}
                      onChange={(e) => handleExpirationChange(index, e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-2 py-1.5 border rounded text-sm ${
                        item.requiresExpiration && !item.expiration
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No items found in this outbound shipment.</p>
          <p className="text-sm text-gray-500 mt-1">
            Please add items to the outbound before creating an inbound plan.
          </p>
        </div>
      )}

      {/* Submit Button */}
      {items.length > 0 && (
        <div className="pt-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || loading}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <div className="flex items-center">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {statusMessage || 'Creating Plan...'}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Create Inbound Plan
                </div>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step2_CreateInboundPlan;
